// src/services/catalogService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { MediaService } from './mediaService';


class CatalogService {
  constructor() {
    this.booksCollection = 'pb-books';
    this.itemsCollection = 'pb-items';
  }

  // ============ BOOK/CATALOG METHODS ============
  
  async createCatalog(catalogData, userId) {
    try {
      const docRef = await addDoc(collection(db, this.booksCollection), {
        ...catalogData,
        userId,
        itemCount: 0,
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      });
      
      return { id: docRef.id, ...catalogData };
    } catch (error) {
      console.error('Error creating catalog:', error);
      throw new Error('Failed to create catalog');
    }
  }

  async updateCatalog(catalogId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, catalogId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating catalog:', error);
      throw new Error('Failed to update catalog');
    }
  }

  async getCatalog(catalogId) {
    try {
      const docRef = doc(db, this.booksCollection, catalogId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Catalog not found');
      }
    } catch (error) {
      console.error('Error getting catalog:', error);
      throw new Error('Failed to get catalog');
    }
  }

  async getUserCatalogs(userId) {
    try {
      const q = query(
        collection(db, this.booksCollection),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const catalogs = [];
      
      querySnapshot.forEach((doc) => {
        catalogs.push({ id: doc.id, ...doc.data() });
      });
      
      return catalogs;
    } catch (error) {
      console.error('Error getting user catalogs:', error);
      throw new Error('Failed to get catalogs');
    }
  }

  // ============ ITEM/PRODUCT METHODS ============
  
  async addItem(bookId, itemData) {
    try {
      const docRef = await addDoc(collection(db, this.itemsCollection), {
        ...itemData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
        views: 0,
        contacts: 0
      });

      // Update book item count
      await this.updateCatalog(bookId, {
        itemCount: increment(1)
      });

      return { id: docRef.id, ...itemData };
    } catch (error) {
      console.error('Error adding item:', error);
      throw new Error('Failed to add item');
    }
  }

  async updateItem(itemId, updates) {
    try {
      const docRef = doc(db, this.itemsCollection, itemId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating item:', error);
      throw new Error('Failed to update item');
    }
  }

  async deleteItem(itemId, bookId) {
    try {
      const docRef = doc(db, this.itemsCollection, itemId);
      await deleteDoc(docRef);

      // Update book item count
      await this.updateCatalog(bookId, {
        itemCount: increment(-1)
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting item:', error);
      throw new Error('Failed to delete item');
    }
  }

  async getItems(bookId, filters = {}) {
    try {
      let q = query(
        collection(db, this.itemsCollection),
        where('bookId', '==', bookId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      const items = [];
      
      querySnapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });

      return items;
    } catch (error) {
      console.error('Error getting items:', error);
      throw new Error('Failed to get items');
    }
  }

  async getItem(itemId) {
    try {
      const docRef = doc(db, this.itemsCollection, itemId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Item not found');
      }
    } catch (error) {
      console.error('Error getting item:', error);
      throw new Error('Failed to get item');
    }
  }

  // ============ BULK OPERATIONS ============
  
  async bulkImportItems(bookId, items) {
    try {
      const batch = writeBatch(db);
      const timestamp = serverTimestamp();
      
      items.forEach((item, index) => {
        const itemRef = doc(collection(db, this.itemsCollection));
        batch.set(itemRef, {
          ...item,
          bookId,
          createdAt: timestamp,
          updatedAt: timestamp,
          status: 'active',
          views: 0,
          contacts: 0,
          order: index
        });
      });

      await batch.commit();

      // Update book item count
      await this.updateCatalog(bookId, {
        itemCount: increment(items.length)
      });

      return { imported: items.length };
    } catch (error) {
      console.error('Error bulk importing items:', error);
      throw new Error('Failed to import items');
    }
  }

  async duplicateItem(itemId) {
    try {
      const item = await this.getItem(itemId);
      const { id, createdAt, updatedAt, ...itemData } = item;
      
      const newItem = {
        ...itemData,
        name: `${itemData.name} (Copy)`,
        status: 'draft'
      };

      return await this.addItem(itemData.bookId, newItem);
    } catch (error) {
      console.error('Error duplicating item:', error);
      throw new Error('Failed to duplicate item');
    }
  }

  // ============ FILE UPLOAD METHODS ============
  
  async uploadImage(file, path) {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  }

  async deleteImage(imageUrl) {
    try {
      if (imageUrl && imageUrl.includes('firebase')) {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw error for image deletion failures
      return { success: false };
    }
  }

  // ============ ANALYTICS METHODS ============
  
  async trackView(bookId, itemId = null) {
    try {
      if (itemId) {
        // Track item view
        await this.updateItem(itemId, {
          views: increment(1)
        });
      }
      
      // Track catalog view
      await this.updateCatalog(bookId, {
        views: increment(1)
      });

      return { success: true };
    } catch (error) {
      console.error('Error tracking view:', error);
      // Don't throw error for analytics failures
      return { success: false };
    }
  }

  async trackContact(bookId, itemId, method = 'unknown') {
    try {
      // Track item contact
      if (itemId) {
        await this.updateItem(itemId, {
          contacts: increment(1)
        });
      }

      // You could add more detailed analytics here
      return { success: true };
    } catch (error) {
      console.error('Error tracking contact:', error);
      return { success: false };
    }
  }

  static async uploadMultipleFiles(files, path, onProgress) {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${index}_${file.name}`;
        const storageRef = ref(storage, `${path}/${fileName}`);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        if (onProgress) {
          onProgress(index + 1, files.length);
        }
        
        return {
          url: downloadURL,
          fileName: file.name,
          fileType: file.type,
          index
        };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return { error: error.message, fileName: file.name, index };
      }
    });

    return await Promise.all(uploadPromises);
  }

  async uploadMultipleImages(files, path, onProgress) {
    try {
      return await MediaService.uploadMultipleFiles(files, path, onProgress);
    } catch (error) {
      console.error('Error uploading multiple images:', error);
      throw new Error('Failed to upload images');
    }
  }

  async uploadVideo(file, path) {
    try {
      const timestamp = Date.now();
      const fileName = `video_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${path}/videos/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        fileName: file.name,
        fileType: file.type,
        type: 'video'
      };
    } catch (error) {
      console.error('Error uploading video:', error);
      throw new Error('Failed to upload video');
    }
  }

  // Delete multiple media files
  async deleteMultipleMedia(mediaUrls) {
    const deletePromises = mediaUrls.map(async (url) => {
      try {
        if (url && url.includes('firebase')) {
          const imageRef = ref(storage, url);
          await deleteObject(imageRef);
        }
        return { success: true, url };
      } catch (error) {
        console.error('Error deleting media:', error);
        return { success: false, url, error: error.message };
      }
    });

    return await Promise.all(deletePromises);
  }

  // Process video URLs (YouTube/Vimeo or uploaded files)
  processVideoUrl(url) {
    const mediaType = MediaService.detectMediaType(url);
    
    switch (mediaType) {
      case 'youtube':
        const youtubeId = MediaService.getYouTubeVideoId(url);
        return {
          type: 'youtube',
          videoId: youtubeId,
          thumbnail: MediaService.generateVideoThumbnail(youtubeId, 'youtube'),
          embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
          originalUrl: url
        };
      
      case 'vimeo':
        const vimeoId = MediaService.getVimeoVideoId(url);
        return {
          type: 'vimeo',
          videoId: vimeoId,
          thumbnail: MediaService.generateVideoThumbnail(vimeoId, 'vimeo'),
          embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
          originalUrl: url
        };
      
      case 'video':
        return {
          type: 'video',
          url: url,
          originalUrl: url
        };
      
      default:
        return {
          type: 'unknown',
          url: url,
          originalUrl: url
        };
    }
  }

  // ============ SEARCH AND FILTER METHODS ============
  
  async searchItems(bookId, searchTerm, filters = {}) {
    try {
      const items = await this.getItems(bookId);
      
      let filteredItems = items;

      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredItems = filteredItems.filter(item =>
          item.name?.toLowerCase().includes(term) ||
          item.brand?.toLowerCase().includes(term) ||
          item.category?.toLowerCase().includes(term) ||
          item.description?.toLowerCase().includes(term)
        );
      }

      // Apply additional filters
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          filteredItems = filteredItems.filter(item => {
            if (key === 'priceRange') {
              const [min, max] = filters[key].split('-').map(Number);
              const price = parseFloat(item.price || 0);
              return price >= min && (max ? price <= max : true);
            }
            return item[key] === filters[key];
          });
        }
      });

      return filteredItems;
    } catch (error) {
      console.error('Error searching items:', error);
      throw new Error('Failed to search items');
    }
  }

  // ============ UTILITY METHODS ============
  
  getUniqueValues(items, field) {
    return [...new Set(items.map(item => item[field]).filter(Boolean))];
  }

  formatPrice(price, currency = 'ZAR') {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(price);
  }

  calculateMonthlyPayment(price, interestRate = 0.105, months = 60) {
    const principal = parseFloat(price);
    const monthlyRate = interestRate / 12;
    
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                   (Math.pow(1 + monthlyRate, months) - 1);
    
    return Math.round(payment);
  }

  // ============ CSV EXPORT/IMPORT ============
  
  async exportToCSV(bookId) {
    try {
      const items = await this.getItems(bookId);
      const catalog = await this.getCatalog(bookId);
      
      // Create CSV headers
      const headers = Object.keys(items[0] || {}).filter(key => key !== 'id');
      const csvContent = [
        headers.join(','),
        ...items.map(item => 
          headers.map(header => `"${item[header] || ''}"`).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${catalog.botName}_catalog.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error exporting CSV:', error);
      throw new Error('Failed to export CSV');
    }
  }

  parseCSV(csvText) {
    const lines = csvText.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
    
    const items = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.replace(/"/g, '').trim());
      const item = {};
      
      headers.forEach((header, index) => {
        item[header] = values[index] || '';
      });
      
      return item;
    });

    return items;
  }
}

export const catalogService = new CatalogService();